<template>
  <CContainer>
    <CRow class="justify-content-center">
      <CCol md="12" class="text-start px-0">
        <CAlert
          v-if="reqResponse.message"
          class="mt-4"
          :color="reqResponse.type"
          :show="!!reqResponse.message"
        >
          {{ reqResponse.message }}
        </CAlert>
        <CForm class="mb-2">
          <div v-if="editableNames">
            <div class="form-group mb-3">
              <input
                type="text"
                readonly
                class="form-control"
                :value="editableNames"
              />
            </div>
          </div>

          <div v-else>
            <VSelect
              v-model="form.limit_for"
              :class="{
                'is-invalid':
                  v$.form.limit_for.$error || reqResponse.errors['limit_for']
              }"
              class="cs-select mb-3 ps__child--consume"
              :options="locationAvailabilityOptions"
              placeholder="For"
              :clearable="false"
              label="label"
              :reduce="(item) => item.value"
              @update:model-value="resetOptinalInputs"
            >
              <template #footer>
                <div
                  v-if="v$.form.$error && v$.form.limit_for.required.$invalid"
                  class="invalid-feedback"
                >
                  This field is required
                </div>
                <div
                  v-if="reqResponse.errors['limit_for']"
                  class="invalid-feedback"
                >
                  {{ reqResponse.errors["limit_for"][0] }}
                </div>
              </template>
            </VSelect>

            <div v-if="form.limit_for === 'student'">
              <LazyLoadSelect
                :classes="[
                  {
                    'is-invalid':
                      v$.form.selected_student_ids.$error ||
                      reqResponse.errors['selected_student_ids']
                  },
                  'mb-3'
                ]"
                :is-multiple="true"
                :selected="form.selected_student_ids"
                type="student"
                placeholder="Select students"
                @changed="
                  (value) => {
                    form.selected_student_ids = value
                  }
                "
              >
                <template #footer>
                  <div
                    v-if="
                      v$.form.$error &&
                      v$.form.selected_student_ids.required.$invalid
                    "
                    class="invalid-feedback"
                  >
                    This field is required
                  </div>
                  <div
                    v-if="reqResponse.errors['selected_student_ids']"
                    class="invalid-feedback"
                  >
                    {{ reqResponse.errors["selected_student_ids"][0] }}
                  </div>
                </template>
              </LazyLoadSelect>
            </div>

            <div v-if="gradeYears && form.limit_for === 'gradeyear'">
              <VSelect
                v-model="form.grade_years"
                :class="{
                  'is-invalid':
                    v$.form.grade_years.$error ||
                    reqResponse.errors['grade_years']
                }"
                class="cs-select mb-3 ps__child--consume"
                multiple
                :clear-search-on-blur="
                  () => {
                    return true
                  }
                "
                :options="gradeYears"
                placeholder="Select grad years"
                :clearable="false"
                @open="getGradeYears"
              >
                <template #footer>
                  <div
                    v-if="
                      v$.form.$error && v$.form.grade_years.required.$invalid
                    "
                    class="invalid-feedback"
                  >
                    This field is required
                  </div>
                  <div
                    v-if="reqResponse.errors['grade_years']"
                    class="invalid-feedback"
                  >
                    {{ reqResponse.errors["grade_years"][0] }}
                  </div>
                </template>
              </VSelect>
            </div>

            <div v-show="form.limit_for === 'csv'" class="mb-3">
              <div class="d-flex align-items-start">
                <div class="form-group me-3 mb-0">
                  <div class="cs-file-input">
                    <div class="upload-btn">
                      <i class="fi flaticon-upload me-1" /> UPLOAD CSV
                    </div>
                    <input
                      type="file"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          v$.form.csv_file.$error ||
                          reqResponse.errors['csv_file']
                      }"
                      @change="onFileUpload"
                    />
                    <div
                      v-if="
                        v$.form.$error && v$.form.csv_file.required.$invalid
                      "
                      class="invalid-feedback"
                    >
                      This field is required
                    </div>

                    <div
                      v-if="v$.form.$error && !v$.form.csv_file.fileValidator"
                      class="invalid-feedback"
                    >
                      The csv file must be a file of type: csv, txt.
                    </div>

                    <div
                      v-if="reqResponse.errors['csv_file']"
                      class="invalid-feedback"
                    >
                      {{ reqResponse.errors["csv_file"][0] }}
                    </div>
                  </div>
                </div>
                <a href="/csv-samples/passLimitSample.csv" class="download-btn">
                  <i class="fi flaticon-download me-1" /> SAMPLE CSV
                </a>
              </div>
              <p v-if="form.csv_file" class="mt-2">
                <b>{{ form.csv_file.name ? form.csv_file.name : "" }}</b>
              </p>
            </div>
          </div>

          <CRow>
            <CCol sm="6" class="pe-3 pe-sm-2">
              <div v-if="startDateCheck">
                <CFormInput
                  v-model="form.start_date"
                  placeholder="Start"
                  disabled
                />
              </div>
              <div v-else>
                <DatePicker
                  v-model="form.start_date"
                  class="cs-date-picker"
                  :masks="{ L: 'MM/DD/YYYY' }"
                  placeholder="Start"
                  :popover="{
                    visibility:
                      isEditableMode && !isEditableStartDate
                        ? 'hidden'
                        : 'click'
                  }"
                  :min-date="minFromDate"
                  :max-date="maxDate"
                  @update:model-value="onStartDateChange"
                >
                  <template #default="{ inputValue, togglePopover }">
                    <div class="input-icon-field" @click="togglePopover">
                      <div class="form-group">
                        <input
                          class="form-control"
                          :class="{
                            'is-invalid':
                              v$.form.start_date.$error ||
                              reqResponse.errors['from_date']
                          }"
                          placeholder="Select start date"
                          readonly
                          :value="inputValue"
                        />
                        <div
                          v-if="
                            v$.form.$error &&
                            v$.form.start_date.required.$invalid
                          "
                          class="invalid-feedback"
                        >
                          This field is required
                        </div>
                        <div
                          v-if="reqResponse.errors['from_date']"
                          class="invalid-feedback"
                        >
                          {{ reqResponse.errors["from_date"][0] }}
                        </div>
                      </div>
                      <span
                        v-show="
                          !isEditableMode ||
                          (isEditableMode && isEditableStartDate)
                        "
                        class="field-icon"
                      >
                        <i class="fi flaticon-calendar me-1" />
                      </span>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </CCol>
            <CCol sm="6" class="ps-3 ps-sm-2">
              <DatePicker
                v-model="form.end_date"
                class="cs-date-picker"
                :masks="{ L: 'MM/DD/YYYY' }"
                placeholder="End"
                :popover="{ visibility: 'click' }"
                :min-date="minToDate"
                :max-date="maxDate"
              >
                <template #default="{ inputValue, togglePopover }">
                  <div class="input-icon-field" @click="togglePopover">
                    <div class="form-group">
                      <input
                        class="form-control"
                        :class="{
                          'is-invalid':
                            v$.form.end_date.$error ||
                            reqResponse.errors['to_date']
                        }"
                        placeholder="Select end date"
                        readonly
                        :value="inputValue"
                      />
                      <div
                        v-if="
                          v$.form.$error && v$.form.end_date.required.$invalid
                        "
                        class="invalid-feedback"
                      >
                        This field is required
                      </div>
                      <div
                        v-if="reqResponse.errors['to_date']"
                        class="invalid-feedback"
                      >
                        {{ reqResponse.errors["to_date"][0] }}
                      </div>
                    </div>
                    <span class="field-icon">
                      <i class="fi flaticon-calendar me-1" />
                    </span>
                  </div>
                </template>
              </DatePicker>
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="6" class="pe-3 pe-sm-2">
              <RecurrenceRadioGroup v-model="form.recurrence_type" />
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="6" class="pe-3 pe-sm-2">
              <div class="form-group">
                <input
                  v-model="form.pass_limit"
                  type="number"
                  class="form-control"
                  :class="{
                    'is-invalid':
                      v$.form.pass_limit.$error || reqResponse.errors['limit']
                  }"
                  placeholder="Pass limit"
                  @input="setPassLimit($event.target.value)"
                />
                <div
                  v-if="v$.form.$error && v$.form.pass_limit.required.$invalid"
                  class="invalid-feedback"
                >
                  This field is required
                </div>
                <div
                  v-if="reqResponse.errors['limit']"
                  class="invalid-feedback"
                >
                  {{ reqResponse.errors["limit"][0] }}
                </div>
              </div>
            </CCol>
            <CCol sm="6" class="ps-3 ps-sm-2">
              <div class="form-group">
                <textarea
                  v-model="form.reason"
                  class="form-control"
                  :class="{
                    'is-invalid':
                      v$.form.reason.$error || reqResponse.errors['reason']
                  }"
                  placeholder="Reason"
                  rows="1"
                />
                <div
                  v-if="reqResponse.errors['reason']"
                  class="invalid-feedback"
                >
                  {{ reqResponse.errors["reason"][0] }}
                </div>
              </div>
            </CCol>
          </CRow>

          <div class="form-group form-actions mt-4 text-center">
            <div class="d-flex align-items-center justify-content-center">
              <div
                v-if="editablePassLimit && editablePassLimit.id"
                class="d-flex"
              >
                <div @click="submit(true)">
                  <CLoadingButton
                    :spinner="isLoading"
                    :disabled="isLoading"
                    :loading="isLoading"
                    class="me-2 px-5 py-3 btn bg-gradient-blue text-white"
                  >
                    Update
                  </CLoadingButton>
                </div>
                <div class="ms-2" @click="cancelEdit()">
                  <CButton class="me-2 btn-gradient-border">
                    <span class="btn px-4"
                      ><b class="text-graident-blue">Cancel</b></span
                    >
                  </CButton>
                </div>
              </div>
              <div v-else @click="submit()">
                <CLoadingButton
                  :spinner="isLoading"
                  :disabled="isLoading"
                  :loading="isLoading"
                  class="me-2 btn px-5 py-3 bg-gradient-blue text-white"
                >
                  Submit
                </CLoadingButton>
              </div>
              <router-link
                v-if="!isInView"
                class="text-graident-blue ms-3"
                to="/limits/limit-student-pass"
              >
                <div class="d-flex align-items-center" @click="goToFullMenu()">
                  <img
                    width="18"
                    class="m-0 me-2"
                    src="@/assets/images/icons/gradient-link.png"
                  />
                  <span class="text-graident-blue">Go to full menu</span>
                </div>
              </router-link>
            </div>
          </div>
        </CForm>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import useVuelidate from "@vuelidate/core"
import { required, requiredIf, helpers } from "vuelidate/lib/validators"
import "vue-select/dist/vue-select.css"
import moment from "moment-timezone"
import LazyLoadSelect from "@/components/LazyLoadSelect.vue"
import RecurrenceRadioGroup from "./RecurrenceRadioGroup.vue"

const fileValidator = (value) =>
  !helpers.req(value) ||
  value.type == "text/csv" ||
  value.type == "text/plain" ||
  value.type == "application/vnd.ms-excel" //"text/plain"
export default {
  name: "CreateLimitStudentPassForm",
  components: {
    LazyLoadSelect,
    RecurrenceRadioGroup
  },
  props: ["isInView", "editablePassLimit"],
  emits: ["CreatePassLimit", "closeModal"],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: false,
      startDateCheck: false,
      form: {
        limit_for: null,
        csv_file: null,
        grade_years: null,
        selected_student_ids: null,
        start_date: null,
        end_date: null,
        pass_limit: null,
        reason: "",
        recurrence_type: null
      },
      locationAvailabilityOptions: [
        { value: "school", label: "All Students" },
        { value: "student", label: "Selected Students" },
        { value: "gradeyear", label: "Grad Year Students" },
        { value: "csv", label: "CSV Student List" }
      ],
      weekDays: [
        {
          label: "Mo",
          value: "Monday"
        },
        {
          label: "Tu",
          value: "Tuesday"
        },
        {
          label: "We",
          value: "Wednesday"
        },
        {
          label: "Th",
          value: "Thursday"
        },
        {
          label: "Fr",
          value: "Friday"
        },
        {
          label: "Sa",
          value: "Saturday"
        },
        {
          label: "Su",
          value: "Sunday"
        }
      ],
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      gradeYears: []
    }
  },
  validations: {
    form: {
      limit_for: {
        required
      },
      start_date: {
        required
      },
      end_date: {
        required
      },
      recurrence_type: {},
      pass_limit: {
        required
      },
      reason: {},
      selected_student_ids: {
        required: requiredIf((model) => {
          return model.limit_for && model.limit_for === "student"
        })
      },
      grade_years: {
        required: requiredIf((model) => {
          return model.limit_for && model.limit_for === "gradeyear"
        })
      },
      csv_file: {
        required: requiredIf((model) => {
          return model.limit_for && model.limit_for === "csv"
        }),
        fileValidator
      }
    }
  },
  computed: {
    isValid() {
      return !this.v$.form.$invalid
    },
    minFromDate() {
      return moment().format("MMM DD YYYY")
    },
    maxDate() {
      return this.$helpers.maxSchoolYearDate()
    },
    minToDate() {
      return this.form.start_date
        ? this.form.start_date
        : moment().format("MMM DD YYYY")
    },
    editableNames() {
      if (this.editablePassLimit) {
        if (this.editablePassLimit.grade_year) {
          return (
            "Grad Year: " + JSON.parse(this.editablePassLimit.grade_year)[0]
          )
        }
        if (this.editablePassLimit.limitable) {
          switch (this.editablePassLimit.limitable_type) {
            case "App\\Models\\School":
              return "School: " + this.editablePassLimit.limitable.name

            case "App\\Models\\User":
              return (
                "Student: " +
                this.editablePassLimit.limitable.first_name +
                " " +
                this.editablePassLimit.limitable.last_name
              )

            default:
              break
          }
        }
      }
      return null
    },
    isEditableStartDate() {
      return moment(this.form.start_date)._d >= moment()._d
    },
    isEditableMode() {
      return !!this.editablePassLimit
    }
  },
  watch: {
    editablePassLimit: function (pass) {
      this.resetForm()
      this.resetResponseMessages()
      this.setFormByPassRecord(pass)
    },
    $route: function () {
      this.cancelEdit()
    }
  },
  methods: {
    setFormByPassRecord(passLimit) {
      if (passLimit) {
        this.form = {
          limit_for: "student",
          csv_file: null,
          grade_years: null,
          start_date: passLimit.from_date ? passLimit.from_date : null,
          end_date: passLimit.to_date ? passLimit.to_date : null,
          pass_limit: passLimit.limit,
          reason: passLimit.reason,
          recurrence_type: passLimit.recurrence_type,
          selected_student_ids: passLimit.limitable_id
        }
        this.startDateCheck = !this.isEditableStartDate
      }
    },
    submit(isUpdate) {
      this.resetResponseMessages()
      if (this.isValid) {
        let formData = null
        const inputsData = {
          limit_for: this.form.limit_for,
          from_date: this.$helpers.currTzDate(this.form.start_date),
          to_date: this.$helpers.currTzDate(this.form.end_date),
          limit: this.form.pass_limit,
          reason: this.form.reason,
          recurrence_type: this.form.recurrence_type //daily,weekly,monthly
        }
        if (this.form.selected_student_ids && !isUpdate) {
          inputsData.selected_student_ids = Array.isArray(
            this.form.selected_student_ids
          )
            ? JSON.stringify(
                this.form.selected_student_ids.map((el) => el.value.id)
              )
            : JSON.stringify(this.form.selected_student_ids)
        }
        if (this.form.grade_years) {
          inputsData.grade_years = JSON.stringify(this.form.grade_years)
        }
        if (this.form.limit_for === "csv") {
          inputsData.csv_file = this.form.csv_file
          formData = new FormData()
          for (const key in inputsData) {
            if (Object.hasOwnProperty.call(inputsData, key)) {
              const element = inputsData[key]
              formData.append(key, element)
            }
          }
        }
        if (isUpdate) {
          this.updatePassLimit(inputsData)
        } else {
          this.createPassLimit(formData ? formData : inputsData)
        }
      } else {
        this.v$.$touch()
      }
    },
    createPassLimit(params) {
      this.isLoading = true
      this.$store
        .dispatch("passLimits/createPassLimit", params)
        .then(() => {
          this.resetForm()
          this.setSuccessResponse("Successfully created.")
          this.isLoading = false
          setTimeout(() => {
            this.resetResponseMessages()
            this.$emit("CreatePassLimit")
          }, 1000)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
        })
    },
    updatePassLimit(params) {
      this.isLoading = true
      const data = {
        params,
        id: this.editablePassLimit.id
      }
      this.$store
        .dispatch("passLimits/updatePassLimit", data)
        .then(() => {
          this.startDateCheck = false
          this.resetForm()
          this.setSuccessResponse("Successfully Updated.")
          this.isLoading = false
          setTimeout(() => {
            this.cancelEdit()
            this.$store.commit("passLimits/SET_EDITABLE_PASS_LIMITS", null)
          }, 1000)
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
        })
    },
    resetOptinalInputs() {
      this.form.csv_file = null
      this.form.grade_years = null
      this.form.selected_student_ids = null
    },
    resetRecurrenceInputs() {
      this.form.recurrence_days = []
      this.form.recurrence_end_at = null
      this.form.recurrence_week = null
    },
    onFileUpload(event) {
      const files = event.target.files
      if (files && files.length) {
        this.form.csv_file = files[0]
      }
      event.target.value = ""
    },
    onStartDateChange() {
      this.form.end_date =
        this.form.end_date && this.form.end_date < this.form.start_date
          ? this.form.start_date
          : this.form.end_date
    },
    onRepeatOptionChange() {
      this.form.recurrence_days = []
      this.form.recurrence_end_at = null
      this.form.recurrence_week = null
    },
    setWeekDay(day) {
      if (this.form.recurrence_days.length) {
        if (this.form.recurrence_days.filter((el) => el === day).length) {
          this.form.recurrence_days = this.form.recurrence_days.filter(
            (el) => el !== day
          )
        } else {
          this.form.recurrence_days.push(day)
        }
      } else {
        this.form.recurrence_days.push(day)
      }
    },
    setPassLimit(value) {
      const numbers = /^[0-9]+$/
      if (value) {
        if (value.match(numbers)) {
          this.form.pass_limit = parseInt(value) > 0 ? parseInt(value) : 0
        } else {
          this.form.pass_limit = ""
        }
      }
    },
    getGradeYears() {
      this.$store.dispatch("passBlocks/getGradeYears").then((result) => {
        this.gradeYears = result.data.data
        // let startYear = result.data.data.startYear;
        // let endYear = result.data.data.endYear;
        // this.gradeYears = [...Array(endYear - startYear + 1).keys()]. map(i => i + startYear);
      })
    },
    resetForm() {
      this.v$.$reset()
      this.form = {
        limit_for: null,
        csv_file: null,
        grade_years: null,
        selected_student_ids: null,
        start_date: null,
        end_date: null,
        pass_limit: null,
        reason: "",
        recurrence_type: null,
        recurrence_days: [],
        recurrence_end_at: null
      }
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setSuccessResponse(message) {
      this.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Someting went wrong!",
        errors: errors ? errors : {}
      }
    },
    cancelEdit() {
      this.$store.commit("passLimits/SET_EDITABLE_PASS_LIMITS", null)
      this.startDateCheck = false
      this.resetForm()
      this.resetResponseMessages()
    },
    refreshForm() {
      this.resetForm()
      this.resetResponseMessages()
    },
    goToFullMenu() {
      this.$emit("closeModal")
    }
  }
}
</script>
